import React, { useState } from "react";
import { IonButton, IonRow, IonCol, IonItem, IonInput, IonCard, IonSearchbar, IonCardTitle } from "@ionic/react";
import { getTrainersByNameGSI } from "../../utilities/trainer/Trainer";
import { Trainer } from "../../models";
import { formatShortAddress } from "../../utilities/address/FormatAddress";
import { getBarnById } from "../../utilities/barn/Barn";
import { formattedOption } from "../../utilities/trainer/SelectOrCreateTrainerUtils";
import BasicModal from "../Modals/BasicModal";

interface VerifyTrainerModalProps {
    currentValue?: formattedOption | null | undefined;
    handleCancel: () => void;
    handleUseMatchedTrainer: (e: formattedOption) => void;
    handleCreateNewTrainer: (newTrainerName: string) => void;
}

const VerifyTrainerModal: React.FC<VerifyTrainerModalProps> = ({ currentValue, handleCancel, handleUseMatchedTrainer, handleCreateNewTrainer }) => {
    // Track the user's confirmed trainer name, which will be used to search the DB for the trainer
    const [unConfirmedTrainerName, setUnConfirmedTrainerName] = useState(currentValue?.trainerName || "");
    const [confirmedTrainerName, setConfirmedTrainerName] = useState("");
    const [isNameConfirmed, setIsNameConfirmed] = useState(false);

    // Store in state all trainers that match the confirmed trainer name in the DB
    const [formattedMatchedTrainers, setFormattedMatchedTrainers] = useState<formattedOption[] | null | undefined>();
    const [filteredFormattedMatchedTrainers, setFilteredFormattedMatchedTrainers] = useState<formattedOption[] | null | undefined>();
    const [searchText, setSearchText] = useState("");

    const [error, setError] = useState("");
    const [isLoading, setIsLoading] = useState(false);

    /**
     * User confirmed the trainer name
     * Search the DB by this trainer name (Server-Side Filtering)
     * Only get the trainer fields needed (Projection)
     */
    const handleClickConfirm = async () => {
        setError("");
        if (unConfirmedTrainerName === "") {
            setError("Please include a trainer name.");
        } else {
            const formattedUnconfirmedName = unConfirmedTrainerName.trim();
            setUnConfirmedTrainerName(formattedUnconfirmedName);
            setConfirmedTrainerName(formattedUnconfirmedName);
            setIsNameConfirmed(true);
            setIsLoading(true);
            const queryResult = await getTrainersByNameGSI(unConfirmedTrainerName, true);
            if (queryResult.isSuccess) {
                const trainersFromDB = queryResult.result;
                const formattedTrainers = await formatTrainers(trainersFromDB);
                setFormattedMatchedTrainers(formattedTrainers);
                setFilteredFormattedMatchedTrainers(formattedTrainers);
            }
            setIsLoading(false);
        }
    };

    const formatTrainers = async (trainers: Trainer[]) => {
        let formatted: formattedOption[] = [];
        for (let i = 0; i < trainers.length; i++) {
            const trainer = trainers[i];
            const formattedTrainer = await formatSingleTrainer(trainer);
            formatted.push(formattedTrainer);
        }
        return formatted;
    };

    const formatSingleTrainer = async (trainer: Trainer) => {
        const value = trainer.id;
        const name = trainer.name || "Unknown - No name found";
        const trainerNameLabel = name || "error";
        const location = trainer.address ? formatShortAddress(trainer.address) || "Unknown" : trainer.location || "Unknown";
        let barnName = "Unknown";
        if (trainer.barnId) {
            const queryBarnResult = await getBarnById(trainer.barnId);
            barnName = queryBarnResult.result?.name || "Unknown";
        }

        let object: formattedOption = {
            value: value,
            label: trainerNameLabel + " " + location + " " + barnName,
            trainerName: name,
            trainerNameLabel: trainerNameLabel,
            locationLabel: location,
            trainer: trainer,
            barnLabel: barnName,
        };
        return object;
    };

    /**
     * Allow user to search the matched trainers by trainer name, barn name
     */
    const handleSearchText = (text: string) => {
        setSearchText(text);
        if (text) {
            // Filter by text
            const filtered = formattedMatchedTrainers?.filter(
                (trainer) =>
                    trainer.trainerName.toLocaleLowerCase().includes(text.toLocaleLowerCase()) ||
                    (trainer.barnLabel && trainer.barnLabel.toLocaleLowerCase().includes(text.toLocaleLowerCase())) ||
                    (trainer.barnLabel && trainer.barnLabel.toLocaleLowerCase().includes(text.toLocaleLowerCase())) ||
                    (trainer.locationLabel && trainer.locationLabel.toLocaleLowerCase().trim().includes(text.toLocaleLowerCase().trim()))
            );
            setFilteredFormattedMatchedTrainers(filtered);
        } else if (text === "" && formattedMatchedTrainers) {
            // Result the filtered list to the list of all formattedMatchedTrainers
            setFilteredFormattedMatchedTrainers(formattedMatchedTrainers);
        } else {
            setFilteredFormattedMatchedTrainers([]);
        }
    };

    /**
     * User found the trainer by name in the RSP Database
     * Send that trainer info back to the parent component
     * And close the modal
     */
    const handleClickUseMatchedTrainer = (formattedTrainer: formattedOption) => {
        handleUseMatchedTrainer(formattedTrainer);
    };

    const handleClickCreateNewTrainer = () => {
        handleCreateNewTrainer(confirmedTrainerName);
    };

    return (
        <BasicModal
            isOpen={true}
            onClose={handleCancel}
            title="Verify Trainer"
            isLoading={isLoading}
            errorMessage={error}
            footerButtons={[
                {
                    text: "Cancel",
                    color: "light",
                    disabled: isLoading,
                    onClick: handleCancel,
                },
            ]}>
            <>
                <IonRow className="ion-justify-content-center">
                    <IonCol sizeXs="12" sizeMd="10" className="text-center">
                        <h4 className="text-info font-weight-bold">{currentValue?.trainerName ? "Is this the trainer's name?" : "What is the trainer's name?"}</h4>
                        <IonItem color="white">
                            <IonInput
                                type="text"
                                value={unConfirmedTrainerName}
                                aria-required={true}
                                onIonChange={(e) => {
                                    setUnConfirmedTrainerName(e.detail.value!);
                                }}
                            />
                        </IonItem>
                        <IonButton className="mt-3" color="success" onClick={() => handleClickConfirm()}>
                            Confirm
                        </IonButton>
                    </IonCol>
                </IonRow>
                {isNameConfirmed && (
                    <>
                        <hr className="mb-0" />
                        {formattedMatchedTrainers && formattedMatchedTrainers.length > 0 ? (
                            // There are trainers that match the confirmed trainer name
                            // Use the filtered trainers to display the options to the user and allow search
                            <>
                                {/* Keep the Search Bar and Match Info at the top level */}
                                <IonRow className="m-0">
                                    <IonCol>
                                        <h4 className="mt-1 mb-0 text-info font-weight-bold">
                                            {formattedMatchedTrainers?.length || 0} Match{formattedMatchedTrainers && formattedMatchedTrainers?.length !== 1 && "es"} for{" "}
                                            {confirmedTrainerName}
                                        </h4>
                                    </IonCol>
                                </IonRow>
                                {formattedMatchedTrainers && formattedMatchedTrainers?.length > 3 && (
                                    <IonRow>
                                        <IonCol className="m-0 p-0">
                                            <IonSearchbar
                                                className="m-0 pr-0"
                                                value={searchText}
                                                placeholder="Search by Name, Location or Barn"
                                                onIonChange={(e) => handleSearchText(e.detail.value!)}
                                                color="white"></IonSearchbar>
                                        </IonCol>
                                    </IonRow>
                                )}
                                {filteredFormattedMatchedTrainers && filteredFormattedMatchedTrainers.length > 0 ? (
                                    <IonRow className="small-scrollable bg-light">
                                        <IonCol>
                                            {filteredFormattedMatchedTrainers.map((formattedTrainer, index) => (
                                                <IonRow key={index}>
                                                    <IonCol className="bg-light p-0">
                                                        <IonCard className="rounded-3 py-1 px-3 mx-0 my-1 bg-dark">
                                                            <IonRow className="ion-align-items-space-between py-0">
                                                                <IonCol sizeXs="12" sizeMd="8" className="p-0 ion-text-left">
                                                                    <IonCardTitle>
                                                                        <h4 className="font-weight-bold text-white text-white ion-text-wrap m-0">{formattedTrainer.trainerName}</h4>
                                                                    </IonCardTitle>
                                                                    <p className="text-white m-0">Barn: {formattedTrainer.barnLabel}</p>
                                                                    <p className="text-white m-0">Location: {formattedTrainer.locationLabel}</p>
                                                                </IonCol>
                                                                <IonCol sizeXs="12" sizeMd="4" className="ion-text-center align-content-center p-0">
                                                                    <IonRow className="ion-justify-content-center">
                                                                        <IonCol sizeXs="8" sizeMd="12" className="p-0">
                                                                            <IonButton
                                                                                className="py-1 m-0"
                                                                                expand="block"
                                                                                color="primary"
                                                                                onClick={() => handleClickUseMatchedTrainer(formattedTrainer)}>
                                                                                Select
                                                                            </IonButton>
                                                                        </IonCol>
                                                                    </IonRow>
                                                                </IonCol>
                                                            </IonRow>
                                                        </IonCard>
                                                    </IonCol>
                                                </IonRow>
                                            ))}
                                        </IonCol>
                                    </IonRow>
                                ) : (
                                    <IonRow>
                                        <IonCol>
                                            <p>There are no options that match this search.</p>
                                        </IonCol>
                                    </IonRow>
                                )}
                            </>
                        ) : (
                            <IonRow>
                                <IonCol className="text-center text-danger">
                                    <p>No matches for this trainer name in the RingSide Pro database.</p>
                                </IonCol>
                            </IonRow>
                        )}
                        <hr className="m-0" />

                        <IonRow className="my-0">
                            <IonCol size="12">
                                <IonRow>
                                    <IonCol>
                                        <h4 className="mt-1 mb-0 text-info font-weight-bold">Can't find the trainer?</h4>
                                    </IonCol>
                                </IonRow>
                                <IonRow>
                                    <IonCol className="p-0">
                                        <IonCard className="rounded-3 py-1 px-3 mx-0 my-1 bg-dark">
                                            <IonRow className="ion-align-items-space-between py-0">
                                                <IonCol sizeXs="12" sizeMd="7" className="p-0 ion-text-left">
                                                    <IonCardTitle>
                                                        <h4 className="font-weight-bold text-white text-white ion-text-wrap m-0">{confirmedTrainerName}</h4>
                                                    </IonCardTitle>
                                                    <p className="text-white ion-text-wrap m-0">Barn: {currentValue?.barnLabel}</p>
                                                </IonCol>
                                                <IonCol sizeXs="12" sizeMd="5" className="p-0 ion-text-center align-content-center">
                                                    <IonButton className="py-1 m-0" color="tertiary" onClick={() => handleClickCreateNewTrainer()}>
                                                        Create New Trainer
                                                    </IonButton>
                                                </IonCol>
                                            </IonRow>
                                        </IonCard>
                                    </IonCol>
                                </IonRow>
                            </IonCol>
                        </IonRow>
                    </>
                )}
            </>
        </BasicModal>
    );
};
export default VerifyTrainerModal;
